/**
 * Subscription Card Component
 *
 * Displays current subscription status and plan details.
 */

import React from 'react';
import {
  Crown,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  ArrowUpRight,
} from 'lucide-react';

const STATUS_CONFIG = {
  active: { label: 'Active', color: '#22c55e', icon: CheckCircle },
  trialing: { label: 'Trial', color: 'var(--accent-primary)', icon: Clock },
  past_due: { label: 'Past Due', color: '#ef4444', icon: AlertTriangle },
  canceled: { label: 'Canceled', color: '#8b919e', icon: AlertTriangle },
  unpaid: { label: 'Unpaid', color: '#ef4444', icon: AlertTriangle },
};

export default function SubscriptionCard({ subscription, isLoading, onUpgrade, onCancel }) {
  if (isLoading) {
    return (
      <div className="subscription-card loading">
        <style>{`
          .subscription-card.loading {
            background: var(--bg-secondary, #111318);
            border: 1px solid var(--border-primary, #23262f);
            border-radius: 12px;
            padding: 40px;
            text-align: center;
            color: var(--text-secondary, #8b919e);
          }
        `}</style>
        Loading subscription...
      </div>
    );
  }

  const status = subscription ? STATUS_CONFIG[subscription.status] || STATUS_CONFIG.active : null;
  const StatusIcon = status?.icon || CheckCircle;

  return (
    <div className="subscription-card">
      <style>{`
        .subscription-card {
          background: var(--bg-secondary, #111318);
          border: 1px solid var(--border-primary, #23262f);
          border-radius: 12px;
          overflow: hidden;
        }

        .card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px;
          border-bottom: 1px solid var(--border-primary, #23262f);
        }

        .card-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin: 0;
        }

        .card-title svg {
          width: 20px;
          height: 20px;
          color: var(--accent-primary, var(--accent-primary));
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
        }

        .status-badge svg {
          width: 14px;
          height: 14px;
        }

        .card-content {
          padding: 20px;
        }

        .plan-info {
          display: flex;
          align-items: flex-end;
          gap: 8px;
          margin-bottom: 20px;
        }

        .plan-name {
          font-size: 28px;
          font-weight: 700;
          color: var(--text-primary, #f0f2f5);
        }

        .plan-price {
          font-size: 16px;
          color: var(--text-secondary, #8b919e);
          margin-bottom: 4px;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-bottom: 20px;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .info-label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: var(--text-secondary, #8b919e);
        }

        .info-label svg {
          width: 12px;
          height: 12px;
        }

        .info-value {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-primary, #f0f2f5);
        }

        .cancel-warning {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          background: rgba(245, 158, 11, 0.1);
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .cancel-warning svg {
          width: 20px;
          height: 20px;
          color: #f59e0b;
          flex-shrink: 0;
        }

        .cancel-warning-text {
          font-size: 13px;
          color: #f59e0b;
        }

        .trial-banner {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          background: rgba(var(--accent-primary-rgb, 99, 102, 241), 0.1);
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .trial-banner svg {
          width: 20px;
          height: 20px;
          color: var(--accent-primary);
          flex-shrink: 0;
        }

        .trial-banner-text {
          font-size: 13px;
          color: var(--accent-primary);
        }

        .trial-days-left {
          font-weight: 600;
        }

        .discount-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 8px;
          background: rgba(245, 158, 11, 0.15);
          color: #f59e0b;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 600;
          margin-left: 8px;
        }

        .cancel-link {
          display: block;
          text-align: center;
          margin-top: 12px;
          font-size: 13px;
          color: var(--text-tertiary, #6b7280);
          cursor: pointer;
          background: none;
          border: none;
          font-family: inherit;
          transition: color 0.15s;
          width: 100%;
        }

        .cancel-link:hover {
          color: var(--text-secondary, #8b919e);
        }

        .upgrade-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 12px;
          background: var(--accent-primary, var(--accent-primary));
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.15s;
          font-family: inherit;
        }

        .upgrade-btn:hover {
          background: var(--accent-hover, #5558e3);
        }

        .upgrade-btn svg {
          width: 16px;
          height: 16px;
        }

        .no-subscription {
          text-align: center;
          padding: 40px 20px;
        }

        .no-subscription-icon {
          width: 48px;
          height: 48px;
          color: var(--text-secondary, #8b919e);
          margin-bottom: 16px;
        }

        .no-subscription-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin: 0 0 8px;
        }

        .no-subscription-text {
          font-size: 14px;
          color: var(--text-secondary, #8b919e);
          margin: 0 0 20px;
        }

        .payment-method {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          background: var(--bg-tertiary, #1a1d24);
          border-radius: 8px;
        }

        .payment-icon {
          width: 40px;
          height: 26px;
          background: var(--bg-secondary, #111318);
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: 600;
          color: var(--text-secondary, #8b919e);
          text-transform: uppercase;
        }

        .payment-details {
          flex: 1;
        }

        .payment-card {
          font-size: 13px;
          font-weight: 500;
          color: var(--text-primary, #f0f2f5);
        }

        .payment-expiry {
          font-size: 12px;
          color: var(--text-secondary, #8b919e);
        }
      `}</style>

      <div className="card-header">
        <h3 className="card-title">
          <Crown />
          Subscription
        </h3>
        {status && (
          <span
            className="status-badge"
            style={{
              color: status.color,
              background: `${status.color}15`,
            }}
          >
            <StatusIcon />
            {status.label}
          </span>
        )}
      </div>

      <div className="card-content">
        {subscription ? (
          <>
            <div className="plan-info">
              <span className="plan-name">
                {subscription.plan_name}
                {subscription.discount && (
                  <span className="discount-badge">
                    {subscription.discount.percent_off}% off
                  </span>
                )}
              </span>
              <span className="plan-price">
                {subscription.amount_formatted}/{subscription.interval}
              </span>
            </div>

            {subscription.status === 'trialing' && subscription.trial_end && (
              <div className="trial-banner">
                <Clock />
                <span className="trial-banner-text">
                  Free trial — <span className="trial-days-left">
                    {Math.max(0, Math.ceil((new Date(subscription.trial_end) - new Date()) / (1000 * 60 * 60 * 24)))} days left
                  </span>
                </span>
              </div>
            )}

            {subscription.cancel_at_period_end && (
              <div className="cancel-warning">
                <AlertTriangle />
                <span className="cancel-warning-text">
                  Your subscription will end on {new Date(subscription.current_period_end).toLocaleDateString()}
                </span>
              </div>
            )}

            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">
                  <Calendar /> Billing Period
                </span>
                <span className="info-value">
                  {new Date(subscription.current_period_start).toLocaleDateString()} - {new Date(subscription.current_period_end).toLocaleDateString()}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">
                  <Calendar /> Next Invoice
                </span>
                <span className="info-value">
                  {subscription.cancel_at_period_end
                    ? 'N/A'
                    : new Date(subscription.current_period_end).toLocaleDateString()
                  }
                </span>
              </div>
            </div>

            {subscription.payment_method && (
              <div className="payment-method">
                <div className="payment-icon">
                  {subscription.payment_method.brand}
                </div>
                <div className="payment-details">
                  <div className="payment-card">
                    **** **** **** {subscription.payment_method.last4}
                  </div>
                  <div className="payment-expiry">
                    Expires {subscription.payment_method.exp_month}/{subscription.payment_method.exp_year}
                  </div>
                </div>
              </div>
            )}

            <button className="upgrade-btn" onClick={onUpgrade} style={{ marginTop: 20 }}>
              <ArrowUpRight />
              Change Plan
            </button>

            {onCancel && !subscription.cancel_at_period_end && (
              <button className="cancel-link" onClick={onCancel}>
                Cancel Subscription
              </button>
            )}
          </>
        ) : (
          <div className="no-subscription">
            <Crown className="no-subscription-icon" />
            <h4 className="no-subscription-title">No Active Subscription</h4>
            <p className="no-subscription-text">
              Choose a plan to unlock all features
            </p>
            <button className="upgrade-btn" onClick={onUpgrade}>
              <ArrowUpRight />
              Choose a Plan
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
